import { CurrencyPipe } from "@angular/common";
import { BasketSummary, CurrentTransactionDetails, INITIATING_MODULE, PROVIDERS, TRANSACTION_TYPE, TransactionDocument, externalTransactionDetails, TaxBreakdown, Customer } from "./mapper-library.model";
import { NumberUtilService } from "./number-util.service";
import { AppLoggerService } from '@pmcretail/common-services/common/app-logger.service';

export class WebReturnMagento2RestV1MapperBuilderClass {

    className = "WebReturnMagento2RestV1MapperBuilderClass";
    private orderDetails: any;
    private webReturnObject: TransactionDocument = {
        id: '',
        orgCode: "",
        storeId: "",
        storeNodeId: "",
        storeNodeStructure: "",
        deviceId: "",
        terminalId: "",
        initiatingModule: "",
        export: false,
        isInStoreOrder: false,
        basket: {},
        basketSummary: {},
        baseCurrency: "",
        customer: {
            id: '',
            email: '',
            firstName: '',
            lastName: '',
            billingAddress: [],
            shippingAddress: [],
            shipmentDetails: [],
            telephone: '',
            postCode: '',
            noCustomerSelected: false
        },
        currentTransactionDetails: {} as CurrentTransactionDetails,
        externalTransactionDetails: [] as externalTransactionDetails[]
    }
    numberUtilService: any;

    constructor(orderDetails: any,
        private currencyPipe?: CurrencyPipe
        ) {
        if (orderDetails) {
            this.orderDetails = orderDetails;
        }
        this.numberUtilService = new NumberUtilService(currencyPipe);
    }

    webReturnCommonMapper() {
        AppLoggerService.logDebug(this.className, "webReturnCommonMapper", "Mapping started...");
        this.webReturnObject['type'] = TRANSACTION_TYPE.WEB_RETURNS;
        this.webReturnObject['datetime'] = this.getUTCDateTime(this.orderDetails.updated_at);
        this.webReturnObject.baseCurrency = this.orderDetails.order_currency_code;
        this.webReturnObject.subsidiaryId = '1';
        this.webReturnObject.export = true;
        this.webReturnObject.initiatingModule = INITIATING_MODULE.EBASKET;
        this.webReturnObject.createdAt = this.getUTCDateTime(this.orderDetails.created_at);
        this.webReturnObject.updatedAt = this.getUTCDateTime(this.orderDetails.updated_at);
        this.webReturnObject.transCompletedAt = this.getUTCDateTime(this.orderDetails.updated_at);
        this.webReturnObject['externalTransactionId'] = this.orderDetails.increment_id;
        AppLoggerService.logDebug(this.className, "webReturnCommonMapper", "Mapping ended...");
        return this;
    }

    product() {
        AppLoggerService.logDebug(this.className, "product", "Mapping started...");
        
        this.webReturnObject.basket['products'] = this.orderDetails.items
            ?.filter(product => product?.base_price)
            .map((product, index) => ({
                description: product.name || '',
                SKU: product.sku || '',
                quantity: product.qty_ordered || 0,
                refundedQuantity: product.qty_refunded || 0,
                price: product.base_price || 0,
                itemLineId: (index + 1).toString(),
                externalItemLineId: product.item_id || (index + 1).toString(),
                totalLinePrice: (product.qty_ordered ?? 0) * (product.base_price ?? 0),
                refundedTotal: product.amount_refunded || 0
            })) || [];
    
        AppLoggerService.logDebug(this.className, "product", "Mapping ended...");
        return this;
    }

    linePromotion() {
        AppLoggerService.logDebug(this.className, "linePromotion", "Mapping started...");
    
        this.webReturnObject.basket['linePromotion'] = this.orderDetails.items
            ?.filter(product => product?.base_price && product.discount_amount)
            .map((product, index) => ({
                trigger: [{ 
                    itemLineId: (index + 1).toString(), 
                    quantity: product.qty_ordered || 0 
                }],
                itemLineId: index + 1,
                promotionId: '',
                displayText: this.orderDetails.coupon_code || this.orderDetails.discount_description || 'Promotion', // TODO: need to check
                promotionType: '',
                promotionAmount: product.discount_amount || 0,
                name: this.orderDetails.coupon_code || this.orderDetails.discount_description || 'Promotion', // TODO: need to check
                promotionInfo: '',
                promotionLinePrice: this.numberUtilService.trimTo2Decimal2(((product.qty_ordered ?? 0) * (product.base_price ?? 0)) - (product.discount_amount || 0))
            })) || [];
    
        AppLoggerService.logDebug(this.className, "linePromotion", "Mapping ended...");
        return this;
    }    
      
    customer() {
        AppLoggerService.logDebug(this.className, "customer", "Mapping started...");
    
        const { orderDetails, webReturnObject } = this;
    
        webReturnObject.customer = {
            id: orderDetails.customer_id ? orderDetails.customer_id.toString() : '',
            customerId: orderDetails.customer_id ? orderDetails.customer_id.toString() : '',
            email: orderDetails.customer_email || '',
            firstName: orderDetails.customer_firstname || '',
            lastName: orderDetails.customer_lastname || '',
            shippingAddress: [],
            billingAddress: []
        } as Customer;
    
        const shippingAssignments = orderDetails.extension_attributes?.shipping_assignments || [];
    
        webReturnObject.customer.shippingAddress = shippingAssignments
            .filter(shipment => shipment?.shipping?.address)
            .map(shipment => {
                const address = shipment.shipping.address;
                return {
                    id: address.customer_address_id ? address.customer_address_id.toString() : '',
                    countryCode: address.country_id || '',
                    address1: address.street?.[0] || '',
                    address2: address.street?.[1] || '',
                    address3: address.street?.[2] || '',
                    city: address.city || '',
                    country: '',
                    postCode: address.postcode || ''
                };
            });
    
        const billingAddress = orderDetails.billing_address;
        if (billingAddress) {
            webReturnObject.customer.billingAddress.push({
                id: billingAddress.customer_address_id ? billingAddress.customer_address_id.toString() : '',
                countryCode: billingAddress.country_id || '',
                address1: billingAddress.street?.[0] || '',
                address2: billingAddress.street?.[1] || '',
                address3: billingAddress.street?.[2] || '',
                city: billingAddress.city || '',
                country: '',
                postCode: billingAddress.postcode || ''
            });
        }
    
        AppLoggerService.logDebug(this.className, "customer", "Mpping ended...");
        return this;
    }    

    isInStoreOrder() {
        AppLoggerService.logDebug(this.className, "isInStoreOrder", "Mapping started...");
        this.webReturnObject.isInStoreOrder = false;
        AppLoggerService.logDebug(this.className, "isInStoreOrder", "Mapping ended...");
        return this;
    }

    externalTransactionDetails(invoiceId: string) {
        AppLoggerService.logDebug(this.className, "externalTransactionDetails", "Mapping started...");
    
        const coupon = this.orderDetails.coupon_code
            ? { name: this.orderDetails.coupon_code, id: this.orderDetails.coupon_code }
            : null;
    
        const externalTransactionDetailsObj = {
            uniqueId: this.orderDetails.increment_id || '',
            providerType: PROVIDERS.MAGENTO2,
            details: { invoiceId: invoiceId?.toString() || '' },
            coupons: coupon ? [coupon] : []
        };
    
        this.webReturnObject.externalTransactionDetails.push(externalTransactionDetailsObj);
    
        AppLoggerService.logDebug(this.className, "externalTransactionDetails", "Mapping ended...");
        return this;
    }

    basketSummary() {
        AppLoggerService.logDebug(this.className, "basketSummary", "Mapping started...");
    
        const { orderDetails, webReturnObject } = this;

        const basketSummary: BasketSummary = {
            discountTotal: orderDetails.discount_amount || 0,
            VATTotal: orderDetails.tax_amount || 0,
            saleTotal: orderDetails.subtotal || 0,
            basketTotal: (orderDetails.subtotal || 0) - (orderDetails.total_online_refunded || 0),
            totalItems: orderDetails.total_qty_ordered ?? 0,
            refundTotal: orderDetails.total_online_refunded || 0,
            shippingTotal: orderDetails.shipping_incl_tax || 0,
            basketTotalWithShippingTotal: orderDetails.total_invoiced || 0,
            webOrder: {
                shippingTotal: orderDetails.shipping_incl_tax || 0,
                basketTotalWithShippingTotal: orderDetails.total_invoiced || 0
            }
        };
    
        webReturnObject.basketSummary = basketSummary;
    
        AppLoggerService.logDebug(this.className, "basketSummary", "Mapping ended...");
        return this;
    }    

    refundRequest() {
        AppLoggerService.logDebug(this.className, "refundRequest", "Mapping started...");
    
        this.webReturnObject.refundRequest = (this.orderDetails.items || [])
            .filter(product => product?.base_price)
            .map((product, index) => ({
                itemLineId: index + 1,
                refundedQuantity: product.qty_refunded || 0,
                refundWithPromotion: true,
                quantity: product.qty_ordered || 0
            }))
            .filter(product => product.refundedQuantity !== 0);
    
        AppLoggerService.logDebug(this.className, "refundRequest", `Mapped ${this.webReturnObject.refundRequest.length} refund items.`);
        AppLoggerService.logDebug(this.className, "refundRequest", "Mapping ended...");
    
        return this;
    }

    updateTaxBreakdown() {
        AppLoggerService.logDebug(this.className, "updateTaxBreakdown", "Mapping started...");
    
        const taxLines = this.orderDetails.items
            ?.filter(product => product?.base_price)
            .map((product, index) => {
                const taxableAmount = product.row_total_incl_tax || 0;
                const VAT = product.tax_percent || 0;
                const preTax = this.numberUtilService.trimTo2Decimal2((taxableAmount * 100) / (100 + VAT));
                const taxLineTotal = this.numberUtilService.trimTo2Decimal2(taxableAmount - preTax);
    
                return {
                    itemLineId: index + 1,
                    VAT,
                    VATCode: '',
                    taxableAmount,
                    PreTax: preTax,
                    taxLineTotal
                };
            }) || [];
    
        this.webReturnObject.basket["taxBreakdown"] = {
            taxLines,
            totalTaxableAmount: this.orderDetails.tax_amount || 0
        } as TaxBreakdown;
    
        AppLoggerService.logDebug(this.className, "updateTaxBreakdown", `Mapped ${taxLines.length} tax lines.`);
        AppLoggerService.logDebug(this.className, "updateTaxBreakdown", "Mapping ended...");
    
        return this;
    }

    deviceId(deviceId) {
        this.webReturnObject.deviceId = deviceId;
        return this;
    }

    terminalId(terminalId) {
        this.webReturnObject.terminalId = terminalId;
        return this;
    }

    orgCode(orgCode) {
        this.webReturnObject.orgCode = orgCode;
        return this;
    }

    storeId() {
        this.webReturnObject.storeId = this.orderDetails.store_id;
        return this;
    }

    storeNodeId(storeNodeId: string) {
        this.webReturnObject.storeNodeId = storeNodeId;
        return this;
    }

    storeNodeStructure(storeNodeStructure: string) {
        this.webReturnObject.storeNodeStructure = storeNodeStructure;
        return this;
    }

    docId(docId) {
        this.webReturnObject.id = docId;
        return this;
    }

    updateUserID(userID: string) {
        this.webReturnObject.userId = userID;
        return this;
    }

    updateUserName(name) {
        this.webReturnObject.userName = name;
        return this;
    }

    build() {
        AppLoggerService.logDebug(this.className, "build", "Mapping done...");
        return this.webReturnObject;
    }

    getUTCDateTime(date) {
        if (date) {
            return new Date(date).toISOString();
        }
    }
}
